import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Divider,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ButtonBase } from "@mui/material";
import { IconEdit, IconTrash, IconPlus } from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";

const EditButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const DeleteButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.error.dark,
  "&:hover": {
    background: theme.palette.error.dark,
    color: theme.palette.error.light,
  },
  width: 40,
  height: 40,
  borderRadius: 12,
  cursor: "pointer",
}));

const AddButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.info.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  align: "center",
  gap: 8,
  width: 150,
  height: 40,
  borderRadius: 10,
  display: "flex",
  cursor: "pointer",
}));

const LawyersList = () => {
  const [lawyers, setLawyers] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentLawyer, setCurrentLawyer] = useState({
    name: "",
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const fetchLawyers = useCallback(() => {
    const searchText = searchTerm !== "" ? `&contains=${searchTerm}` : "";
    fetch(
      `/backend/lawyers/?page=${page + 1}&page_size=${rowsPerPage}${searchText}`
    )
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = "Não foi possível carregar a lista de advogados. Tente novamente.";
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.detail) {
              errorMessage = errorData.detail;
            }
          } catch (e) {
            console.warn("Não foi possível extrair mensagem de erro específica:", e);
          }
          throw new Error(errorMessage);
        }
        return response.json();
      })
      .then((data) => {
        setLawyers(data.results);
        setTotalRecords(data.count);
      })
      .catch((error) => {
        console.error("Erro ao carregar advogados:", error);
        alert(error.message);
      });
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchLawyers();
  }, [fetchLawyers, page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (lawyer) => {
    setCurrentLawyer(lawyer);
    setIsEditing(true);
    setOpenModal(true);
    setFieldErrors({});
  };

  const handleAdd = () => {
    setCurrentLawyer({
      name: "",
    });
    setIsEditing(false);
    setOpenModal(true);
    setFieldErrors({});
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFieldErrors({});
  };

  const handleCloseWithConfirmation = () => {
    if (window.confirm("Deseja realmente fechar? Todas as alterações não salvas serão descartadas.")) {
      setOpenModal(false);
    }
  };

  const handleSave = () => {
    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/lawyers/${currentLawyer.id}/`
      : "/backend/lawyers/";

    fetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(currentLawyer),
    })
      .then(async (response) => {
        if (response.ok) return response.json();
        
        // Tenta extrair os erros específicos de cada campo do backend
        const errors = {};
        let generalError = null;
        
        try {
          const errorData = await response.json();
          
          // Mapeia os erros do backend para os campos do formulário
          const fieldMapping = {
            name: 'name'
          };
          
          // Processa erros de campos específicos
          Object.keys(fieldMapping).forEach(backendField => {
            if (errorData[backendField] && Array.isArray(errorData[backendField])) {
              errors[fieldMapping[backendField]] = errorData[backendField][0];
            }
          });
          
          // Erros gerais
          if (errorData.error) {
            generalError = errorData.error;
          } else if (errorData.non_field_errors && errorData.non_field_errors[0]) {
            generalError = errorData.non_field_errors[0];
          } else if (errorData.detail) {
            generalError = errorData.detail;
          }
          
          // Se houver erros de campos, atualiza o estado
          if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
          }
          
          // Monta mensagem de erro geral
          if (generalError) {
            throw new Error(generalError);
          } else if (Object.keys(errors).length > 0) {
            throw new Error("Por favor, corrija os erros nos campos destacados.");
          } else {
            throw new Error("Não foi possível salvar o advogado. Verifique os dados e tente novamente.");
          }
        } catch (e) {
          // Se não conseguir parsear o JSON, usa a mensagem padrão
          if (e.message) {
            throw e;
          }
          console.warn("Não foi possível extrair mensagem de erro específica:", e);
          throw new Error("Não foi possível salvar o advogado. Verifique os dados e tente novamente.");
        }
      })
      .then((data) => {
        if (isEditing) {
          setLawyers((prevLawyers) =>
            prevLawyers.map((lawyer) => (lawyer.id === data.id ? data : lawyer))
          );
        } else {
          setLawyers((prevLawyers) => [...prevLawyers, data]);
        }
        alert("Advogado cadastrado com sucesso!");
        setOpenModal(false);
        setFieldErrors({});
        fetchLawyers();
      })
      .catch((error) => {
        console.error("Erro ao salvar advogado:", error);
        alert(error.message);
      });
  };

  const handleDelete = (lawyerId) => {
    if (window.confirm("Deseja realmente excluir este advogado? Esta ação não pode ser desfeita.")) {
      fetch(`/backend/lawyers/${lawyerId}/`, {
        method: "DELETE",
      })
        .then(async (response) => {
          if (response.ok) {
            setLawyers((prevLawyers) =>
              prevLawyers.filter((lawyer) => lawyer.id !== lawyerId)
            );
            alert("Advogado removido com sucesso!");
            fetchLawyers();
          } else {
            // Tenta extrair a mensagem de erro específica do backend
            let errorMessage = "Não foi possível excluir o advogado. Verifique se não há vínculos com outros registros.";
            try {
              const errorData = await response.json();
              if (errorData.error) {
                errorMessage = errorData.error;
              } else if (errorData.detail) {
                errorMessage = errorData.detail;
              }
            } catch (e) {
              // Se não conseguir parsear o JSON, usa a mensagem padrão
              console.warn("Não foi possível extrair mensagem de erro específica:", e);
            }
            throw new Error(errorMessage);
          }
        })
        .catch((error) => {
          console.error("Erro ao excluir advogado:", error);
          alert(error.message);
        });
    }
  };

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title="Cadastro de advogados"
        actions={[
          <AddButton onClick={handleAdd}>
            <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
            Adicionar advogado
          </AddButton>,
        ]}
      >
        <SearchSection onSearchChange={setSearchTerm} />
        <Divider sx={{ my: 2 }} />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lawyers.map((lawyer) => (
              <TableRow key={lawyer.id}>
                <TableCell>{lawyer.name}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <EditButton onClick={() => handleEdit(lawyer)}>
                      <IconEdit stroke={1.5} size="1.3rem" />
                    </EditButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <DeleteButton onClick={() => handleDelete(lawyer.id)}>
                      <IconTrash stroke={1.5} size="1.3rem" />
                    </DeleteButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MainCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Advogados por página"
      />

      <Dialog 
        open={openModal} 
        onClose={(event, reason) => {
          // Impede o fechamento do modal ao clicar fora
          if (reason === 'backdropClick') {
            return;
          }
          // Para tecla ESC, solicita confirmação
          if (reason === 'escapeKeyDown') {
            handleCloseWithConfirmation();
            return;
          }
          handleCloseModal();
        }}
      >
        <DialogTitle>
          {isEditing ? "Editar Advogado" : "Adicionar Advogado"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={12}>
            {/* Nome */}
            <Grid item xs={12}>
              <TextField
                label="Nome"
                value={currentLawyer.name}
                onChange={(e) => {
                  setCurrentLawyer({ ...currentLawyer, name: e.target.value });
                  // Limpa o erro do campo quando o usuário começa a digitar
                  if (fieldErrors.name) {
                    setFieldErrors({ ...fieldErrors, name: null });
                  }
                }}
                fullWidth
                sx={{ mt: 2 }}
                autoComplete="new-password"
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || ""}
                required
              />
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LawyersList;
